import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { ColorPicker } from '@progress/kendo-react-inputs';
import { SVGIcon } from '@progress/kendo-svg-icons';
import React, { useMemo } from 'react';
import { UNIVERSAL_MAP_COLOR_PALETTE_SETTINGS } from '../../../../../../../common/color-picker-palette';
import { ExtractKeysExtend } from '../../../../../../../components/helpers/helpers';
import {
    AddTextOutline,
    ArrowUpOutline,
    BulletedListOutline,
    CircleDottedOutline,
    CommentOutline,
    EmailOutline,
    ExpandOutline,
    FileOutline,
    FillColorOutline,
    MapOutline,
    MinimizeOutline,
    NotificationOutline,
    PlayCircleOutline,
    ScreenOutline,
    UserGroupOutline
} from '../../../../../../../icons/icons';
import { AircraftThresholdMergeResult } from '../../../../../../../monitoring/merger/aircraft-threshold-merger';
import { AirportThresholdMergeResult } from '../../../../../../../monitoring/merger/airport-threshold-merger';
import { MonitorThresholdAlertMinimumLevelOptions } from '../../../../../../../types/dropdown-options';
import {
    AircraftThresholdType,
    AirportThresholdType,
    CompanyThresholdsMode,
    MonitorAircraftThreshold,
    MonitorAircraftThresholdAlerts,
    MonitorAirportThresholdAlerts,
    MonitorAirportThresholdSettingsItem,
    MonitorThresholdAlert,
    MonitorThresholdAlertMinimumLevel,
    ThresholdInfo
} from '../../../../../../../types/thresholds';
import { CheckboxThreeState } from '../../../../../../components/inputs/CheckboxThreeState';
import ColorPickerWithDefault from '../../../../../../components/inputs/ColorPickerWithDefault';
import { DropdownListWithValueField } from '../../../../../../components/inputs/DropdownListWithValueField';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/store-hooks';
import { setSetting } from '../../../../../../state/slices/settings-slice';

export const alertDefinitions: Record<
    keyof NonNullable<(AircraftThresholdMergeResult & AirportThresholdMergeResult)['definition']>['alerts'],
    { name: string; icon: SVGIcon | null }
> = {
    eventList: { name: 'Event list', icon: BulletedListOutline },
    eventListAck: { name: 'Ack', icon: null },
    eventListImportant: { name: '(!) Important indicator', icon: null },
    email: { name: 'Email', icon: EmailOutline },
    logFile: { name: 'Log file', icon: FileOutline },
    osAlert: { name: 'OS alert', icon: NotificationOutline },
    popup: { name: 'Popup alert', icon: ScreenOutline },
    aircraftTag: { name: 'Turn on aircraft tag', icon: CommentOutline },
    zoomToAircraft: { name: 'Zoom to aircraft', icon: MapOutline },
    overrideColor: { name: 'Override aircraft color', icon: FillColorOutline },
    enableMarker: { name: 'Enable marker around aircraft', icon: CircleDottedOutline },
    voice: { name: 'Voice alert', icon: AddTextOutline },
    playSound: { name: 'OS/Windows sounds', icon: PlayCircleOutline },

    alertOnActive: { name: 'Active', icon: null },
    alertOnPlanned: { name: 'Planned', icon: null },
    alertOnScheduled: { name: 'Scheduled', icon: null }
} as const;

export type AlertItemChangeArgs<T> = {
    name: Extract<ExtractKeysExtend<T, MonitorThresholdAlert>, string>;
    value: boolean | null;
    minLevel: MonitorThresholdAlertMinimumLevel | null;
};

const AlertItemContext = React.createContext<{
    enforceMinimum: boolean;
    inheritanceEnabled: boolean;
    detailedView: boolean;
}>({
    enforceMinimum: false,
    detailedView: false,
    inheritanceEnabled: false
});

const AlertItem = <T,>(props: {
    alerts: { definition: T | null; inherited: T | null; effective: T | null };
    name: Extract<ExtractKeysExtend<T, MonitorThresholdAlert>, string>;
    hideMinLevel?: boolean;
    className?: string;
    onEdit?: (e: AlertItemChangeArgs<T>) => void;
}) => {
    const { enforceMinimum, inheritanceEnabled, detailedView } = React.useContext(AlertItemContext);
    const alertInfo = alertDefinitions[props.name];

    const definition = (props.alerts.definition?.[props.name] ?? null) as MonitorThresholdAlert | null;
    const inherited = (props.alerts.inherited?.[props.name] ?? null) as MonitorThresholdAlert | null;
    const effective = (props.alerts.effective?.[props.name] ?? null) as MonitorThresholdAlert | null;

    const checked: boolean | null = ((enforceMinimum ? effective?.value || definition?.value : definition?.value) ?? null) as
        | boolean
        | null;
    const disabled = enforceMinimum && inherited?.value === true;

    const maxMinLevel: MonitorThresholdAlertMinimumLevel | null = (enforceMinimum ? inherited?.minLevel : null) ?? null;
    const displayOptions = useMemo(
        () =>
            MonitorThresholdAlertMinimumLevelOptions.map((x) => ({
                ...x,
                disabled: maxMinLevel !== null && x.value !== null && x.value > maxMinLevel
            })),
        [maxMinLevel]
    );

    return (
        <div className="d-flex gap-1 justify-content-between align-items-center" style={{ height: 30 }}>
            <CheckboxThreeState
                className={props.className}
                checked={checked}
                disabled={disabled}
                onChange={(e) => props.onEdit?.({ name: props.name, value: e.value, minLevel: definition?.minLevel ?? null })}
                label={
                    <span>
                        {alertInfo.icon && <SvgIcon className="icon-b-1" icon={alertInfo.icon} />}
                        <span> {alertInfo.name}</span>
                    </span>
                }
            />

            {!props.hideMinLevel && detailedView ? (
                <div className="d-flex gap-2">
                    {inheritanceEnabled && (
                        <div className="d-flex gap-2" style={{ width: 150 }}>
                            {inherited && (inherited.value || inherited.minLevel !== null) ? (
                                <span>
                                    {inherited.value && <span>Enabled </span>}
                                    <span>
                                        {inherited.minLevel !== null && (
                                            <>
                                                <SvgIcon icon={ArrowUpOutline} />
                                                {MonitorThresholdAlertMinimumLevel[inherited.minLevel]}
                                            </>
                                        )}
                                    </span>
                                </span>
                            ) : (
                                <span>-</span>
                            )}
                        </div>
                    )}
                    <DropdownListWithValueField
                        value={definition?.minLevel ?? null}
                        style={{ height: 25, width: 125 }}
                        size="small"
                        data={displayOptions}
                        onChange={(e) => props.onEdit?.({ name: props.name, value: definition?.value ?? null, minLevel: e.value })}
                    />
                </div>
            ) : (
                <></>
                // TODO: fix this a bit, it shows clear when there are nulls
                // <div>{effective && effective?.minLevel !== null ? '>= ' + MonitorThresholdAlertMinimumLevel[effective.minLevel] : ''}</div>
            )}
        </div>
    );
};

export function AircraftAlertEditView(props: {
    tab: 'user' | 'company';
    type: AircraftThresholdType;
    threshold: AircraftThresholdMergeResult;
    info: ThresholdInfo;
    mode: CompanyThresholdsMode;
    onEdit?: (type: AircraftThresholdType, recipe: (draft: MonitorAircraftThreshold) => void) => void;
}) {
    const handleEdit = (e: AlertItemChangeArgs<MonitorAircraftThresholdAlerts>) => {
        props.onEdit?.(props.type, (x) => (x.alerts[e.name] = { value: e.value, minLevel: e.minLevel }));
    };

    const alerts = {
        definition: props.threshold.definition?.alerts ?? null,
        inherited: props.threshold.inherited?.alerts ?? null,
        effective: props.threshold.effective?.alerts ?? null
    };

    return (
        <EditAlertFrame tab={props.tab} mode={props.mode} info={props.info}>
            <div>
                <AlertItem alerts={alerts} onEdit={handleEdit} name="eventList" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="eventListAck" className="ms-2" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="eventListImportant" className="ms-2" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="email" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="logFile" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="osAlert" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="popup" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="zoomToAircraft" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="aircraftTag" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="overrideColor" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="enableMarker" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="voice" />
                <AlertItem alerts={alerts} onEdit={handleEdit} name="playSound" />

                <div className="secondary-text mt-2">Flight status</div>
                <AlertItem alerts={alerts} onEdit={handleEdit} hideMinLevel name="alertOnScheduled" />
                <AlertItem alerts={alerts} onEdit={handleEdit} hideMinLevel name="alertOnPlanned" />
                <AlertItem alerts={alerts} onEdit={handleEdit} hideMinLevel name="alertOnActive" />

                <div className="secondary-text mt-2">Aircraft and event list indicator color</div>
                <ColorPickerWithDefault
                    defaultColor={alerts.definition?.overrideColorHex ?? alerts.inherited?.overrideColorHex ?? ''}
                    views={['gradient', 'palette']}
                    value={alerts.definition?.overrideColorHex ?? alerts.inherited?.overrideColorHex ?? ''}
                    onChange={(e) => props.onEdit?.(props.type, (x) => (x.alerts.overrideColorHex = e.value))}
                />
            </div>
        </EditAlertFrame>
    );
}

export function AirportAlertEditView(props: {
    tab: 'user' | 'company';
    type: AirportThresholdType;
    mode: CompanyThresholdsMode;
    info: ThresholdInfo;
    alerts: {
        definition: MonitorAirportThresholdAlerts | null;
        inherited: MonitorAirportThresholdAlerts | null;
        effective: MonitorAirportThresholdAlerts;
    };
    onEdit?: (type: AirportThresholdType, recipe: (draft: MonitorAirportThresholdSettingsItem) => void) => void;
}) {
    const handleEdit = (e: AlertItemChangeArgs<MonitorAirportThresholdAlerts>) => {
        props.onEdit?.(props.type, (x) => (x.alerts[e.name] = { value: e.value, minLevel: e.minLevel }));
    };

    return (
        <EditAlertFrame tab={props.tab} mode={props.mode} info={props.info}>
            <div>
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="eventList" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="eventListAck" className="ms-2" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="eventListImportant" className="ms-2" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="email" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="logFile" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="osAlert" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="popup" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="overrideColor" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="voice" />
                <AlertItem alerts={props.alerts} onEdit={handleEdit} name="playSound" />

                <div className="secondary-text mt-2">Event list indicator color</div>
                <ColorPicker
                    views={['gradient', 'palette']}
                    paletteSettings={UNIVERSAL_MAP_COLOR_PALETTE_SETTINGS}
                    value={props.alerts.definition?.overrideColorHex ?? props.alerts.inherited?.overrideColorHex ?? ''}
                    onChange={(e) => props.onEdit?.(props.type, (x) => (x.alerts.overrideColorHex = e.value))}
                />
            </div>
        </EditAlertFrame>
    );
}

function EditAlertFrame(props: { tab: 'user' | 'company'; mode: CompanyThresholdsMode; info: ThresholdInfo; children?: React.ReactNode }) {
    const enforceMinimum = props.mode === CompanyThresholdsMode.minimum;
    const inheritanceEnabled =
        props.tab === 'user' && (props.mode === CompanyThresholdsMode.template || props.mode === CompanyThresholdsMode.minimum);
    const detailedView = useAppSelector((x) => x.settings.monitorThresholdEditorAlertDetailedView) && !props.info.alertsOnly;
    const showMinLevel = !props.info.alertsOnly;
    const dispatch = useAppDispatch();

    return (
        <AlertItemContext.Provider
            value={useMemo(
                () => ({ enforceMinimum, detailedView, inheritanceEnabled }),
                [enforceMinimum, detailedView, inheritanceEnabled]
            )}>
            <div className="p-2 d-flex flex-column gap-2" style={{ width: detailedView ? (inheritanceEnabled ? 550 : 400) : 300 }}>
                <div>
                    <div className="secondary-text d-flex gap-2">
                        <div className="flex-grow-1">Alerts</div>
                        {inheritanceEnabled && detailedView && (
                            <div className="d-flex gap-1" style={{ width: 150 }}>
                                <SvgIcon icon={UserGroupOutline} />
                                <div>Company {props.mode === CompanyThresholdsMode.minimum ? 'minimum' : 'template'}</div>
                            </div>
                        )}
                        {detailedView && <div style={{ width: 125 }}>Min alert level</div>}
                    </div>
                    {props.children}
                </div>
                {showMinLevel && (
                    <Button
                        onClick={() => dispatch(setSetting('monitorThresholdEditorAlertDetailedView', !detailedView))}
                        svgIcon={detailedView ? MinimizeOutline : ExpandOutline}>
                        {detailedView ? 'Simple view' : 'Detailed view'}
                    </Button>
                )}
            </div>
        </AlertItemContext.Provider>
    );
}
